/**
 * Resolve qual ticketeira este servidor atende.
 *
 * No app nativo a resposta vem do flavor, fixada na compilação. Aqui há um
 * build só para todas as empresas, e quem diz a ticketeira é o `config.js` que
 * o container escreve na partida (ver vite.config.ts). Duas travas cobrem o
 * erro que isso abre — o de configurar um servidor com a empresa de outro:
 *
 * - `config.js` vazio não cai numa empresa padrão: vira erro na tela.
 * - Página servida pelo domínio de uma ticketeira e configurada com outra
 *   também vira erro. Endereço que não é de ninguém (localhost, o domínio de
 *   teste da SaveinCloud) passa.
 *
 * A API sai do cadastro com o endereço trocado por `/rest/4.5/` relativo: o
 * navegador só fala com a própria origem, e quem leva até o servidor de
 * verdade é a infraestrutura. O `cliente.ts` não sabe disso.
 */

import {TICKETEIRAS, type Ticketeira} from './ticketeiras';
import {txt} from '../ui/idioma';

declare global {
  interface Window {
    /**
     * O que o servidor diz sobre este endereço: qual ticketeira ele atende e,
     * quando o endereço é de outro país, em que idioma abrir (ver
     * `src/ui/idioma.ts`).
     */
    TMOBI_CONFIG?: {ticketeira?: string; idioma?: string};
  }
}

const API_RELATIVA = '/rest/4.5/';

const semWww = (dominio: string) => dominio.replace(/^www\./, '').toLowerCase();

/**
 * Duas falhas diferentes, que pareciam a mesma.
 *
 * O `config.js` é uma tag `<script>` no index.html. Quando o servidor está
 * fora do ar — reimplantação da imagem, queda de rede — ele não carrega e
 * `TMOBI_CONFIG` fica indefinido; o app abre assim mesmo, porque o service
 * worker guardou o resto. Quando carrega mas vem vazio, aí sim é o container
 * sem a variável, e nenhuma espera resolve.
 *
 * Antes as duas diziam "confira a variável TICKETEIRA do container", o que
 * mandava a portaria mexer na configuração no meio de uma reimplantação de
 * trinta segundos.
 */
function idConfigurado(): string {
  const config = typeof window !== 'undefined' ? window.TMOBI_CONFIG : undefined;

  if (!config) {
    throw new Error(
      txt(
        'O servidor não respondeu. Se a versão está sendo atualizada agora, ' +
          'espere um instante e tente de novo.',
      ),
    );
  }
  if (!config.ticketeira) {
    throw new Error(
      txt(
        'Este servidor não diz qual ticketeira atende (config.js sem "ticketeira"). ' +
          'Confira a variável TICKETEIRA do container.',
      ),
    );
  }
  return config.ticketeira;
}

/** A ticketeira dona do endereço desta página, se for de alguma. */
function donaDoEndereco(): Ticketeira | undefined {
  if (typeof location === 'undefined') {
    return undefined;
  }
  const host = semWww(location.hostname);
  return Object.values(TICKETEIRAS).find(t => {
    const dominio = semWww(t.dominio);
    return host === dominio || host.endsWith('.' + dominio);
  });
}

let escolhida: Ticketeira | null = null;

export function ticketeira(): Ticketeira {
  if (escolhida) {
    return escolhida;
  }

  const id = idConfigurado();
  const encontrada = TICKETEIRAS[id];

  if (!encontrada) {
    throw new Error(`Ticketeira "${id}" não existe em src/config/ticketeiras.ts.`);
  }

  const dona = donaDoEndereco();
  if (dona && dona.id !== encontrada.id) {
    throw new Error(
      `Este endereço é da ${dona.nome}, mas o servidor está configurado para ` +
        `${encontrada.nome}. Confira a variável TICKETEIRA do container.`,
    );
  }

  escolhida = {...encontrada, apiBase: API_RELATIVA};
  return escolhida;
}

export {SEMANTICAS, TICKETEIRAS} from './ticketeiras';
export type {Ticketeira} from './ticketeiras';
